"use client";

import type { ButtonProps as BaseButtonProps } from "@heroui/react";
import { Button as BaseNextButton, extendVariants } from "@heroui/react";
import Image from "next/image";
import { forwardRef, useMemo } from "react";

import { cn, tailwindMergeConfig } from "@/utils/common/styleHelper/utils";
import { checkExistValue } from "@/utils/common/validateHelper/validateUtils";

import LoadingGother from "../global/Loading/LoadingGother";
import type {
  ColorButtonType,
  GroupType,
  SizeButtonType,
  ThemeButtonType,
  VariantButtonType,
} from "./BaseEnum";
import {
  ColorButtonEnum,
  SizeButtonEnum,
  ThemeButtonEnum,
  VariantButtonEnum,
} from "./BaseEnum";

const texts: VariantButtonType[] = [
  VariantButtonEnum.TEXT,
  VariantButtonEnum.LINK,
];
const sizes: SizeButtonType[] = Object.values(SizeButtonEnum);
const colors: ColorButtonType[] = Object.values(ColorButtonEnum);

const BaseButton = extendVariants(
  BaseNextButton,
  {
    variants: {
      defaultVariant: {
        default: cn(
          "flex items-center justify-center font-bold data-[hover=true]:!opacity-100 duration-300 group/button",
          "data-[loading=true]:!text-transparent data-[loading=true]:opacity-100 *:data-[loading=true]:opacity-0",
          "group-[&[role='group']]/groupWrapper:h-full group-[&[role='group']]/groupWrapper:first:rounded-none group-[&[role='group']]/groupWrapper:first:rounded-s-lg",
          "group-[&[role='group']]/groupWrapper:last:rounded-none group-[&[role='group']]/groupWrapper:last:rounded-e-lg",
          "group-[&[role='group']]/groupWrapper:[&:not(:first-child):not(:last-child)]:rounded-none",
        ),
      },
      variant: {
        bordered: cn("border-1"),
        text: cn(""),
        link: cn(""),
      },
      color: {
        red: cn(
          " bg-gradient-to-r from-gradientColorStops-red2-0 to-gradientColorStops-red2-100 text-white ",
          "data-[selected=true]:from-main-red-darker data-[selected=true]:to-main-red-darker ",
        ),
        warmGrey: cn(
          `bg-gradient-to-r from-main-warmGrey-base to-main-warmGrey-base text-black `,
          "data-[selected=true]:from-gradientColorStops-red2-0 data-[selected=true]:to-gradientColorStops-red2-100 data-[selected=true]:text-white",
        ),
        lightGrey: cn(
          `bg-gradient-to-r from-lightGrey to-lightGrey text-black`,
          "data-[selected=true]:from-gradientColorStops-red-0 data-[selected=true]:to-gradientColorStops-red-100 data-[selected=true]:text-white",
        ),
        darkGrey: cn(
          "bg-gradient-to-r from-darkGrey to-darkGrey text-white",
          "data-[selected=true]:from-gradientColorStops-red-0 data-[selected=true]:to-gradientColorStops-red-100 data-[selected=true]:text-black",
        ),
        black: cn(
          "bg-gradient-to-r from-gradientColorStops-black-0 to-gradientColorStops-black-100 text-white",
        ),
        white: cn(
          "bg-gradient-to-r from-white to-white text-primaryMainColor",
          "data-[selected=true]:from-lighterGrey data-[selected=true]:to-lighterGrey",
        ),
      },
      isGroup: {
        true: cn(
          "first:rounded-none first:rounded-s-lg",
          "last:rounded-none last:rounded-e-lg",
          "middle:rounded-none",
        ),
        first: cn("rounded-none rounded-s-lg"),
        last: cn("rounded-none rounded-e-lg"),
        middle: cn("rounded-none"),
        false: "",
      },
      radius: {
        none: "rounded-none",
        full: "rounded-full",
        lg: "rounded-lg",
        md: "rounded-md",
        sm: "rounded-sm",
      },
      disabled: {
        true: "cursor-not-allowed pointer-events-none",
        false: "",
      },
      size: {
        xl: "h-[56px] p-4 text-base gap-2",
        lg: "h-[56px] p-4 text-base gap-2",
        md: "h-[48px] px-3 py-4 text-base gap-2",
        sm: "h-[40px] px-3 py-2 text-xs gap-1",
        xs: "h-[24px] px-3 py-2 text-xxs gap-0.5",
        xxs: "h-[40px] px-3 py-2 text-xxs gap-0.5",
        largeIcon: "size-16 min-w-16 min-h-16",
        mediumIcon: "size-12 min-w-12 min-h-12",
        smallIcon: "size-10 min-w-10 min-h-10",
      },
      disableHover: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "bordered",
        color: "red",
        class: cn(
          "from-white to-white border-gradientColorStops-red-50 text-primaryMainColor",
          "data-[selected=true]:from-main-red-lighter data-[selected=true]:to-main-red-lighter data-[selected=false]:border-lightGrey",
        ),
      },
      {
        variant: "bordered",
        color: "warmGrey",
        class: cn(
          "from-white to-white border-main-warmGrey-base",
          "data-[selected=true]:border-primaryMainColor data-[selected=true]:text-black data-[selected=true]:from-main-warmGrey-base data-[selected=true]:to-main-warmGrey-base",
        ),
      },
      {
        variant: "bordered",
        color: "lightGrey",
        class: cn(
          "from-white to-white border-lightGrey",
          "data-[selected=true]:border-primaryMainColor data-[selected=true]:text-primaryMainColor data-[selected=true]:from-main-red-lighter data-[selected=true]:to-main-red-lighter",
        ),
      },
      {
        variant: "bordered",
        color: "darkGrey",
        class: cn(
          "text-black from-white to-white border-darkGrey ",
          "data-[selected=true]:border-primaryMainColor data-[selected=true]:text-primaryMainColor data-[selected=true]:from-main-red-lighter data-[selected=true]:to-main-red-lighter",
        ),
      },
      {
        variant: "bordered",
        color: "black",
        class: cn(
          "text-black from-white to-white border-darkGrey ",
          "data-[selected=true]:from-lighterGrey data-[selected=true]:to-lighterGrey",
        ),
      },
      {
        variant: "bordered",
        color: "white",
        class: cn(
          " from-white to-white border-gradientColorStops-red-50 ",
          "data-[selected=true]:from-lighterGrey data-[selected=true]:to-lighterGrey",
        ),
      },
      {
        variant: "flat",
        color: "red",
        class: cn(
          "from-status-red-3 to-status-red-3  text-primaryMainColor",
          "data-[selected=true]:from-main-red-lighter data-[selected=true]:to-main-red-lighter ",
        ),
      },
      {
        variant: "flat",
        color: "warmGrey",
        class: cn(
          "data-[selected=true]:from-warmGrey-base data-[selected=true]:to-warmGrey-base ",
        ),
      },
      {
        variant: "flat",
        color: "lightGrey",
        class: cn(
          "data-[selected=true]:from-lighterGrey data-[selected=true]:to-lighterGrey ",
        ),
      },
      {
        variant: "flat",
        color: "darkGrey",
        class: cn(
          "data-[selected=true]:from-lightGrey data-[selected=true]:to-lightGrey",
        ),
      },
      {
        variant: "flat",
        color: "black",
        class: cn(
          "from-white to-white text-black",
          "data-[selected=true]:from-lighterGrey data-[selected=true]:to-lighterGrey",
        ),
      },
      {
        variant: "flat",
        color: "white",
        class: cn(
          "from-white to-white text-primaryMainColor",
          "data-[selected=true]:from-lighterGrey data-[selected=true]:to-lighterGrey",
        ),
      },
      {
        variant: texts,
        size: sizes,
        class: "h-fit min-h-0 min-w-0 p-0",
      },
      {
        variant: texts,
        color: colors,
        class: "from-none to-none",
      },
      {
        variant: texts,
        color: "red",
        class: "text-primaryMainColor",
      },
      {
        variant: texts,
        color: "warmGrey",
        class: "text-main-warmGrey-base",
      },
      {
        variant: texts,
        color: "lightGrey",
        class: "text-lightGrey",
      },
      {
        variant: texts,
        color: "darkGrey",
        class: "text-darkGrey",
      },
      {
        variant: texts,
        color: "black",
        class: "text-black",
      },
      {
        variant: texts,
        color: "white",
        class: "text-white",
      },
      {
        defaultVariant: "default",
        radius: "full",
        class: cn(
          "group-[&[role='group']]/groupWrapper:first:rounded-s-full",
          "group-[&[role='group']]/groupWrapper:last:rounded-e-full",
        ),
      },
      {
        disableHover: false,
        color: "red",
        class: "md:hover:from-main-red-darker md:hover:to-main-red-darker",
      },
      {
        disableHover: false,
        color: "white",
        class: "md:hover:from-lighterGrey md:hover:to-lighterGrey",
      },
      {
        disableHover: false,
        color: "warmGrey",
        class:
          "md:hover:from-gradientColorStops-red2-0 md:hover:to-gradientColorStops-red2-100 md:hover:text-white",
      },
      {
        disableHover: false,
        color: "lightGrey",
        class:
          "md:hover:from-gradientColorStops-red-0 md:hover:to-gradientColorStops-red-100 md:hover:text-white",
      },
      {
        disableHover: false,
        color: "darkGrey",
        class:
          "md:hover:from-gradientColorStops-red-0 md:hover:to-gradientColorStops-red-100",
      },
      {
        disableHover: false,
        variant: "bordered",
        color: "red",
        class: cn(
          "md:hover:from-main-red-lighter md:hover:to-main-red-lighter",
        ),
      },
      {
        disableHover: false,
        variant: "bordered",
        color: "warmGrey",
        class: cn(
          "md:hover:border-primaryMainColor md:hover:text-black md:hover:from-main-warmGrey-base md:hover:to-main-warmGrey-base",
        ),
      },
      {
        disableHover: false,
        variant: "bordered",
        color: "lightGrey",
        class:
          "md:hover:border-primaryMainColor md:hover:text-primaryMainColor md:hover:from-main-red-lighter md:hover:to-main-red-lighter",
      },
      {
        disableHover: false,
        variant: "bordered",
        color: "darkGrey",
        class:
          "md:hover:border-primaryMainColor md:hover:text-primaryMainColor md:hover:from-main-red-lighter md:hover:to-main-red-lighter",
      },
      {
        disableHover: false,
        variant: "bordered",
        color: "black",
        class: "md:hover:from-lighterGrey md:hover:to-lighterGrey",
      },
      {
        disableHover: false,
        variant: "bordered",
        color: "white",
        class: "md:hover:from-lighterGrey md:hover:to-lighterGrey",
      },
      {
        variant: "flat",
        color: "red",
        disableHover: false,
        class: cn(
          "md:hover:from-main-red-lighter md:hover:to-main-red-lighter ",
        ),
      },
      {
        variant: "flat",
        color: "warmGrey",
        disableHover: false,
        class: cn(
          "md:hover:from-main-warmGrey-base md:hover:to-main-warmGrey-base md:hover:text-black",
        ),
      },
      {
        variant: "flat",
        color: "lightGrey",
        disableHover: false,
        class: cn(
          "md:hover:from-lighterGrey md:hover:to-lighterGrey md:hover:text-black",
        ),
      },
      {
        variant: "flat",
        color: "darkGrey",
        disableHover: false,
        class: cn(
          "md:hover:from-lightGrey md:hover:to-lightGrey md:hover:text-black",
        ),
      },
      {
        variant: "flat",
        color: "black",
        disableHover: false,
        class: cn("md:hover:from-lighterGrey md:hover:to-lighterGrey"),
      },
      {
        variant: "flat",
        color: "white",
        disableHover: false,
        class: cn("md:hover:from-lighterGrey md:hover:to-lighterGrey"),
      },
      {
        disableHover: false,
        variant: texts,
        class: "md:hover:from-none md:hover:to-none",
      },
      {
        disableHover: false,
        variant: texts,
        color: "red",
        class: "md:hover:text-main-red-darker",
      },
      {
        disableHover: false,
        variant: texts,
        color: "warmGrey",
        class: "md:hover:text-white",
      },
      {
        disableHover: false,
        variant: texts,
        color: "darkGrey",
        class: "md:hover:text-black",
      },
      {
        disableHover: false,
        variant: texts,
        color: "white",
        class: "md:hover:text-lighterGrey",
      },
      {
        color: colors,
        variant: "solid",
        disabled: true,
        class: "from-lighterGrey  to-lighterGrey text-grey",
      },
      {
        color: colors,
        variant: "flat",
        disabled: true,
        class: "from-lighterGrey  to-lighterGrey text-grey",
      },
      {
        color: colors,
        variant: "bordered",
        disabled: true,
        class: "border-lightGrey from-lighterGrey  to-lighterGrey  text-grey",
      },
      {
        color: colors,
        variant: "text",
        disabled: true,
        class: "from-none  to-none text-grey",
      },
      {
        color: colors,
        variant: "link",
        disabled: true,
        class: "from-none  to-none text-grey",
      },
    ],
    defaultVariants: {
      radius: "lg",
      color: "red",
      variant: "solid",
      size: SizeButtonEnum.LARGE,
      disableHover: false,
      isGroup: false,
      defaultVariant: "default",
      disabled: false,
    },
  },
  {
    twMerge: true,
    twMergeConfig: tailwindMergeConfig,
  },
);

export interface ButtonProps
  extends Omit<BaseButtonProps, "size" | "color" | "variant"> {
  icon?: string;
  disableHover?: boolean;
  isSelected?: boolean;
  theme?: ThemeButtonType;
  size?: SizeButtonType | BaseButtonProps["size"];
  color?: ColorButtonType | BaseButtonProps["color"];
  variant?: VariantButtonType | BaseButtonProps["variant"];
  isGroup?: GroupType;
  classNames?: {
    innerWrapper?: string;
    wrapper?: string;
  };
}
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      icon,
      className,
      children,
      isLoading,
      isSelected,
      type = "button",
      startContent,
      endContent,
      isDisabled,
      disableRipple,
      disabled,
      ...props
    },
    ref,
  ) => {
    const { theme, radius, variant, color } = props;
    const { newRadius, newColor, newVariant, newClassName } = useMemo((): {
      newRadius: ButtonProps["radius"] | undefined;
      newColor: ButtonProps["color"] | undefined;
      newVariant: ButtonProps["variant"] | undefined;
      newClassName?: string;
    } => {
      const getRadius = (rad: ButtonProps["radius"]) => radius ?? rad;
      const getColor = (col: ButtonProps["color"]) => color ?? col;
      const getVariant = (vnt: ButtonProps["variant"]) => variant ?? vnt;
      switch (theme) {
        case ThemeButtonEnum.PRIMARY:
          return {
            newRadius: getRadius("lg"),
            newColor: getColor(ColorButtonEnum.RED),
            newVariant: getVariant("solid"),
          };
        case ThemeButtonEnum.PILL:
          return {
            newRadius: getRadius("full"),
            newColor: getColor(ColorButtonEnum.WARMGREY),
            newVariant: getVariant("solid"),
            newClassName: cn("font-normal"),
          };
        case ThemeButtonEnum.SECONDARY:
          return {
            newRadius: getRadius(getRadius("lg")),
            newColor: getColor(ColorButtonEnum.RED),
            newVariant: getVariant("bordered"),
          };
        case ThemeButtonEnum.SECONDARY_ALT:
          return {
            newRadius: getRadius(getRadius("lg")),
            newColor: getColor(ColorButtonEnum.LIGHTGREY),
            newVariant: getVariant("bordered"),
            newClassName: cn("font-normal"),
          };
        case ThemeButtonEnum.SECONDARY_DARKGREY:
          return {
            newRadius: getRadius(getRadius("lg")),
            newColor: getColor(ColorButtonEnum.DARKGREY),
            newVariant: getVariant("bordered"),
          };
        case ThemeButtonEnum.TEXT:
        case ThemeButtonEnum.LINK:
          return {
            newRadius: getRadius("none"),
            newColor: color,
            newVariant: getVariant("text"),
          };
        default:
          return {
            newRadius: radius,
            newColor: color,
            newVariant: variant,
          };
      }
    }, [theme, radius, variant, color]);

    return (
      <BaseButton
        {...props}
        disableRipple={
          variant === "text" || variant === "link" ? true : disableRipple
        }
        ref={ref as any}
        radius={newRadius}
        variant={newVariant}
        color={newColor}
        type={type}
        data-selected={isSelected}
        data-disable-hover={isSelected}
        className={cn(newClassName, className)}
        isDisabled={disabled ?? isDisabled}
        disabled={disabled ?? isDisabled}
        isLoading={isLoading}
        spinner={
          <LoadingGother
            isOverlay={false}
            className="absolute left-1/2 top-1/2 flex min-w-[200px] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden object-cover !opacity-100"
            imageClass="max-w-[200px]"
          />
        }
      >
        {checkExistValue(startContent) && startContent}
        {checkExistValue(icon) && (
          <Image
            width={16}
            height={16}
            src={`https://d31jk6ljx7wihh.cloudfront.net/icons/${icon}.svg`}
            alt={icon as string}
            className="mr-2"
          />
        )}
        {children}
        {checkExistValue(endContent) && endContent}
      </BaseButton>
    );
  },
);
Button.displayName = "Button";
export default Button;
